import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { env } from '@/config/env';

/**
 * Maintenance window state, served publicly by margarita-foundation (`GET /system/maintenance`).
 * While a window is active the auth screens (login, sign-up, password reset…) are replaced by
 * the maintenance notice. Fail-open: if the state cannot be read, nothing is blocked.
 */

export interface MaintenanceWindow {
  id: string;
  /** language code → text, e.g. { vi: '…', en: '…' } */
  message: Record<string, string>;
  starts_at: string;
  ends_at: string;
}

export interface MaintenanceState {
  maintenance: boolean;
  active: MaintenanceWindow | null;
  upcoming: MaintenanceWindow | null;
  server_time: string;
}

/** Whether the gate is configured for this build. */
export const maintenanceEnabled = Boolean(env.maintenanceUrl);

const LAST_KEY = 'maintenance:last';

/** Last state that was read successfully — used only if the next read fails mid-release. */
function readLast(): MaintenanceState | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as MaintenanceState;
    // Only trust it while its own window can still be running.
    if (s.maintenance && s.active && new Date(s.active.ends_at).getTime() > Date.now()) return s;
  } catch {
    /* ignore */
  }
  return null;
}

async function fetchMaintenance(): Promise<MaintenanceState | null> {
  try {
    const res = await axios.get(env.maintenanceUrl, {
      timeout: 4000,
      // Never served from a browser/CDN cache: the answer flips the moment staff change it. A query
      // parameter, not a request header: the API's CORS allow-list does not include Cache-Control.
      params: { t: Date.now() },
    });
    const data = (res.data?.data ?? null) as MaintenanceState | null;
    if (!data || typeof data.maintenance !== 'boolean') return null;
    try {
      localStorage.setItem(LAST_KEY, JSON.stringify(data));
    } catch {
      /* private mode */
    }
    return data;
  } catch {
    // Backend unreachable (likely being released): keep an active window locked rather than
    // let people into a half-restarted system; otherwise fail open.
    return readLast();
  }
}

/** Polls the state. `data` is undefined until the first read; null = unknown → not blocked. */
export function useMaintenance(refetchMs = 30_000) {
  return useQuery<MaintenanceState | null>({
    queryKey: ['maintenance', 'state'],
    queryFn: fetchMaintenance,
    enabled: maintenanceEnabled,
    refetchInterval: refetchMs,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/** Message in the visitor's language, falling back to English, Vietnamese, then anything. */
export function pickMessage(message: Record<string, string> | undefined, lang = navigator.language): string {
  if (!message) return '';
  const l = lang.toLowerCase();
  return message[l] ?? message[l.split('-')[0]] ?? message.en ?? message.vi ?? Object.values(message)[0] ?? '';
}
